/**
 * @fileoverview Segmented proportion bar shared across sales pages.
 * @description Renders a row of colored segments sized by each value's share of the total — used
 * by the Dashboard's summary cards (pipeline rating mix, target status mix, client status mix) and
 * My Page's "Overall Achievement Progress" bar. Segments with a zero value render nothing, so an
 * empty category doesn't leave a sliver.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

interface Segment {
  /** A stable key for the segment's locator, so it does not change with the UI language. */
  id?: string
  label: string
  value: number
  color: string
}

interface SegmentedBarProps {
  segments: Segment[]
  height?: 'sm' | 'md'
  /** The bar's corners: fully rounded (the default) or the slightly rounded My Page style. */
  corners?: 'pill' | 'soft'
  /** `data-testid` of the bar; each segment gets it plus `-` and its `id`, or its label in kebab case. */
  testId?: string | undefined
}

/**
 * @description A label in kebab case, for a segment's test id ("Awaiting Payment" becomes
 * "awaiting-payment").
 * @param {string} label - The segment's label.
 * @returns {string} The kebab-case label.
 */
const kebab = (label: string) => label.toLowerCase().replace(/\s+/g, '-')

const HEIGHT_CLASS_NAMES: Record<NonNullable<SegmentedBarProps['height']>, string> = {
  sm: 'h-2',
  md: 'h-5',
}

/**
 * @component
 * @param {SegmentedBarProps} props - The segments to render and the bar's height.
 * @param {Segment[]} props.segments - Each segment's label (used as its title tooltip), value, and
 * color class, and optionally the `id` its locator uses instead of the label.
 * @param {'sm' | 'md'} [props.height] - "sm" (default, dashboard cards) or "md" (My Page's
 * standalone progress bar).
 * @param {string} [props.testId] - `data-testid` of the bar; a segment adds its id or kebab-case label.
 */
function SegmentedBar({ segments, height = 'sm', corners = 'pill', testId }: SegmentedBarProps) {
  const total = segments.reduce((sum, segment) => sum + segment.value, 0)

  return (
    <div
      data-testid={testId}
      className={`flex overflow-hidden bg-input-bg ${corners === 'soft' ? 'rounded' : 'rounded-full'} ${HEIGHT_CLASS_NAMES[height]}`}
    >
      {segments
        .filter((segment) => segment.value > 0)
        .map((segment) => (
          <div
            key={segment.id ?? segment.label}
            data-testid={testId ? `${testId}-${segment.id ?? kebab(segment.label)}` : undefined}
            title={`${segment.label} ${segment.value}`}
            style={{ width: total ? `${(segment.value / total) * 100}%` : 0 }}
            className={segment.color}
          />
        ))}
    </div>
  )
}

export default SegmentedBar
